import type { ChipFamilyType } from '@/components/Families'
import type { SubdepAndFamilies } from '@/types/treeViewTypes'
import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'

export function useProductsHeaderFamilies(subdepartments: SubdepAndFamilies[] = []) {
  const navigate = useNavigate()
  const { season, universeId, departmentId } = useParams()
  const [searchParams] = useSearchParams()
  const [isFamiliesModalOpen, setIsFamiliesModalOpen] = useState(false)

  const ids = searchParams.getAll('families')

  const multiFamilies = useMemo(() => {
    return ids && ids.length > 1
  }, [ids])

  const chipFamilies = useMemo(() => {
    if (!subdepartments || subdepartments.length === 0)
      return []

    const formattedProducts: ChipFamilyType[] = subdepartments.flatMap(
      subdept =>
        subdept.families.map(family => ({
          id: family.id,
          labels: family.labels,
          subdepartmentId: subdept.id,
          subdepartmentLabels: subdept.labels,
        })),
    )

    const uniqueFamiliesMap = new Map<number, typeof formattedProducts[0]>()

    formattedProducts.forEach((family) => {
      if (!uniqueFamiliesMap.has(family.id)) {
        uniqueFamiliesMap.set(family.id, family)
      }
    })
    return Array.from(uniqueFamiliesMap.values())
  }, [subdepartments])

  const onValidate = async (families: typeof chipFamilies) => {
    if (families.length === 0) {
      return await navigate(
        `/${season}/universe/${universeId}/department/${departmentId}`,
      )
    }

    const selectedids = families.map(family => family.id.toString())
    const urlSearchParams = new URLSearchParams()

    selectedids.forEach(id => urlSearchParams.append('families', id))
    await navigate({
      pathname: `/${season}/universe/${universeId}/department/${departmentId}/subdepartments/families`,
      search: urlSearchParams.toString(),
    })
  }

  return {
    isFamiliesModalOpen,
    setIsFamiliesModalOpen,
    ids,
    multiFamilies,
    chipFamilies,
    onValidate,
  }
}
